export interface LocalClockEnvironment {
 monotonic():number;wall():number;timeOrigin():number;visible():boolean;
 window:Pick<EventTarget,'addEventListener'|'removeEventListener'>;
 document:Pick<EventTarget,'addEventListener'|'removeEventListener'>;
}
type LocalClockDiagnostic='precision'|'hidden'|'origin'|'invalid_reading'|'read_precision'|'monotonic_rollback'|'wall_rollback'|'observation_gap'|'wall_gap'|'lifecycle'|'closed';
/** Observed browser elapsed-time semantics, not oscillator certification. A bounded
 * read probe rejects unsupported/coarse precision; every use checks wall/elapsed
 * consistency and document lifetime. A retired generation can never resume. */
export class LocalElapsedClock {
 readonly generation=crypto.randomUUID();
 readonly precisionMs:number;
 private retired=false;
 private firstFailure?:LocalClockDiagnostic;
 private previous?:{before:number;after:number;wall:number};
 private origin:number;
 private cleanups:(()=>void)[]=[];
 private env:LocalClockEnvironment;
 constructor(env?:LocalClockEnvironment){
  this.env=env??{monotonic:()=>performance.now(),wall:()=>Date.now(),timeOrigin:()=>performance.timeOrigin,visible:()=>document.visibilityState==='visible',window,document};
  this.origin=this.env.timeOrigin();let precision=0,failure:LocalClockDiagnostic='precision';
  try{
   if(!Number.isFinite(this.origin)||this.origin<0){failure='origin';throw Error();}
   let previous=this.env.monotonic(),changes=0;const began=this.env.wall();
   for(let i=0;i<4096&&changes<3;i++){
    const next=this.env.monotonic(),delta=next-previous;
    if(!Number.isFinite(next)||delta<0||delta>1||this.env.wall()-began>20)throw Error();
    if(delta>0){precision=Math.max(precision,delta);changes++;}previous=next;
   }
   if(changes<3||precision<=0)throw Error();
   const retire=(event:Event)=>{if(event.type!=='pageshow'||(event as PageTransitionEvent).persisted!==false)this.retire('lifecycle');};
   for(const event of ['pagehide','pageshow','freeze','resume']){this.env.window.addEventListener(event,retire);this.cleanups.push(()=>this.env.window.removeEventListener(event,retire));}
   this.env.document.addEventListener('visibilitychange',retire);this.cleanups.push(()=>this.env.document.removeEventListener('visibilitychange',retire));
  }catch{this.retire(failure);}
  this.precisionMs=precision;
  this.current();
 }
 current(){
  if(this.retired)return false;
  let failure:LocalClockDiagnostic='invalid_reading';
  try{
   const e=this.env,before=e.monotonic(),wall=e.wall(),after=e.monotonic(),p=this.previous;
   // Keep the original predicate order and the same captured readings. The
   // first cause is diagnostic only; no later check can requalify this clock.
   if(!e.visible()){failure='hidden';throw Error();}
   if(e.timeOrigin()!==this.origin){failure='origin';throw Error();}
   if(![before,after,wall].every(v=>Number.isFinite(v)&&v>=0&&v<Number.MAX_SAFE_INTEGER/2)||!Number.isSafeInteger(wall))throw Error();
   if(after<before){failure='monotonic_rollback';throw Error();}
   if(after-before>1){failure='read_precision';throw Error();}
   if(p){
    if(before<p.after){failure='monotonic_rollback';throw Error();}
    if(wall<p.wall){failure='wall_rollback';throw Error();}
    if(after-p.after>=1000){failure='observation_gap';throw Error();}
    if(wall+1+2*this.precisionMs<p.wall+before-p.after||wall>p.wall+1+after-p.before+2*this.precisionMs){failure='wall_gap';throw Error();}
   }
   this.previous={before,after,wall};return true;
  }catch{this.retire(failure);return false;}
 }
 diagnostic(){return this.firstFailure;}
 close(){this.retire('closed');}
 private retire(cause:LocalClockDiagnostic){if(this.retired)return;this.firstFailure=cause;this.retired=true;for(const stop of this.cleanups.splice(0))stop();}
}
